'use client';

/**
 * Плитки первого экрана.
 *
 * **Две семьи метрик, и они не смешиваются.** Телефонные — принято,
 * пропущено, ожидание — есть у любого звонка с первой секунды. Качество
 * есть только там, где узлу назначен чек-лист и звонок дошёл до конца
 * обработки. Сведя их в один ряд, мы бы получили ряд, половина которого
 * пуста у половины клиентов, и пустота читалась бы как «плохо».
 *
 * Три места, где такой экран легче всего соврёт, и что здесь сделано:
 *
 * 1. **Пустое качество объясняется словами.** `quality.reason` говорит, чего
 *    именно нет: чек-листа или оценённых звонков. «Средний балл 0» вместо
 *    этого — обвинение в том, чего мы не проверяли.
 * 2. **Доля при нулевом знаменателе — прочерк.** `answered.rate: null`
 *    значит «не звонили», а не «не отвечали».
 * 3. **Пропущено и потеряно — разные вещи.** Крупно показываем `lost`:
 *    окно закрылось и не перезвонил никто. Это единственная цифра, которой
 *    руководитель управляет; `open` рядом и помельче — там ещё могут
 *    перезвонить.
 */

import React from 'react';
import {
  Phone,
  PhoneMissed,
  PhoneOutgoing,
  Clock,
  Gauge,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Minus,
  Loader2,
  CircleSlash,
  Cog,
} from 'lucide-react';
import { count, decimal, duration, percent, periodDays, plural } from '@/lib/format';
import type { DashboardMetrics, QualityReason } from '@/lib/api/types';

/**
 * Сравнение с прошлым отрезком **той же длины**, а не с «прошлым месяцем»:
 * на десятое число сравнение десяти дней с тридцатью показало бы обвал
 * любого показателя.
 *
 * `null` с любой стороны — сравнивать нечего, и мы молчим, а не рисуем ноль.
 */
function Delta({
  current,
  previous,
  suffix = '',
  goodWhenUp = true,
  digits = 0,
}: {
  current: number | null;
  previous: number | null | undefined;
  suffix?: string;
  goodWhenUp?: boolean;
  digits?: number;
}) {
  if (current === null || previous === null || previous === undefined) return null;

  const diff = current - previous;
  if (Math.abs(diff) < Math.pow(10, -digits) / 2) {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-slate-400">
        <Minus size={12} />
        без изменений
      </span>
    );
  }

  const good = diff > 0 === goodWhenUp;
  return (
    <span
      className={`flex items-center gap-1 text-xs font-medium ${
        good ? 'text-green-600' : 'text-red-500'
      }`}
    >
      {diff > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      {diff > 0 ? '+' : '−'}
      {Math.abs(diff).toFixed(digits)}
      {suffix}
    </span>
  );
}

function Tile({
  icon,
  iconClass,
  label,
  value,
  sub,
  delta,
  tone,
  progress,
  className,
}: {
  icon: React.ReactNode;
  iconClass: string;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  delta?: React.ReactNode;
  tone?: 'danger' | 'warning';
  /** Доля 0..1. Полоса под числом — только там, где у него есть потолок. */
  progress?: number | null;
  /**
   * Ширина в колонках сетки.
   *
   * Нужна затем, чтобы ряд замостился нацело: пять плиток в сетке на четыре
   * колонки оставляют пятую одну, а две сетки с разным числом колонок не
   * совпадают по вертикали — и то, и другое читается как поломка вёрстки.
   */
  className?: string;
}) {
  return (
    <div
      className={`card p-5 ${className ?? ''} ${
        tone === 'danger' ? 'border-red-200' : tone === 'warning' ? 'border-amber-200' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconClass}`}>
          {icon}
        </div>
        {delta}
      </div>
      <div className="text-2xl font-bold tabular-nums text-slate-800">{value}</div>
      <div className="text-xs font-medium text-slate-500 mt-0.5">{label}</div>
      {progress !== null && progress !== undefined && (
        <div className="mt-3 w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all"
            style={{ width: `${Math.min(100, Math.max(0, progress * 100))}%` }}
          />
        </div>
      )}
      {sub && <div className="text-[11px] text-slate-400 mt-2 leading-relaxed">{sub}</div>}
    </div>
  );
}

const qualityReasons: Record<QualityReason, string> = {
  no_scorecard: 'чек-лист не назначен ни одному подразделению',
  nothing_scored: 'чек-лист назначен, но за период ничего не оценено',
};

export default function MetricTiles({ data }: { data: DashboardMetrics }) {
  const previous = data.previous;
  const days = periodDays(data.period);
  const comparedTo = previous ? `к предыдущим ${plural(days, 'дню', 'дням', 'дням')}` : '';

  /**
   * Пропущенные, не попавшие ни в один из пяти исходов: строку в журнале
   * восстановления пишет фоновая проверка, и до её прохода звонок нигде не
   * учтён. Считать его «потерянным» нельзя — окно могло и не закрыться.
   */
  const inQueue = data.processing.queued + data.processing.processing;

  /**
   * Все плитки одного размера — так их и читают, сравнивая глазами.
   *
   * Растягивается только качество и только тогда, когда недозвона нет: без
   * него плиток семь, и в ряды по четыре они не ложатся. С недозвоном их
   * восемь, то есть ровно два ряда, и растягивать нечего.
   *
   * Класс написан целиком: собранное из кусков имя Tailwind вырезает из
   * сборки.
   */
  const wideQuality = data.noAnswer ? '' : 'xl:col-span-2';

  const unclassified =
    data.missed.undetermined ??
    Math.max(
      0,
      data.missed.count -
        (data.missed.recoveredByClient +
          data.missed.recoveredByUs +
          data.missed.manual +
          data.missed.open +
          data.missed.lost)
    );

  return (
    <div className="space-y-4">
      {previous && (
        <p className="text-[11px] text-slate-400">
          Сравнение — {comparedTo}: отрезок той же длины, а не прошлый календарный месяц.
        </p>
      )}

      {/* Все плитки — одной сеткой на четыре колонки.

          Двумя сетками было хуже: при разном числе колонок карточки не
          совпадают по вертикали, и экран выглядит сломанным. Ряды мостятся
          нацело за счёт ширины плиток: без недозвона это 4 + (2+1+1), с ним —
          4 + (2+2) + (2+2). Пустых мест не остаётся ни в одном случае. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Tile
          icon={<Phone size={18} className="text-brand-light" />}
          iconClass="bg-blue-50"
          label="Звонков за период"
          value={count(data.calls.total)}
          delta={<Delta current={data.calls.total} previous={previous?.calls.total} />}
          sub={
            <>
              входящих {count(data.calls.inbound)} · исходящих {count(data.calls.outbound)} ·
              внутренних {count(data.calls.internal)}
            </>
          }
        />

        <Tile
          icon={<Phone size={18} className="text-green-600" />}
          iconClass="bg-green-50"
          label="Принято"
          /* Прочерк, а не 0%: null значит «не звонили», а не «не отвечали». */
          value={percent(data.answered.rate, 1)}
          progress={data.answered.rate}
          delta={
            <Delta
              current={data.answered.rate === null ? null : data.answered.rate * 100}
              previous={
                previous?.answered.rate === null || previous?.answered.rate === undefined
                  ? previous?.answered.rate
                  : previous.answered.rate * 100
              }
              suffix=" п.п."
              digits={1}
            />
          }
          /* Доля от входящих, а не от всех: исходящий «отвечен», когда трубку
             взял клиент, и в показатель приёма звонков он не входит. Раньше
             входил — и цифра была про обзвон, а читалась как про приём. */
          sub={
            data.answered.rate === null
              ? 'За период не было ни одного входящего'
              : `${count(data.answered.count)} из ${count(data.calls.inbound)} входящих`
          }
        />

        <Tile
          icon={<PhoneMissed size={18} className="text-red-500" />}
          iconClass="bg-red-50"
          label="Потеряно — не перезвонил никто"
          value={count(data.missed.lost)}
          tone={data.missed.lost > 0 ? 'danger' : undefined}
          delta={
            <Delta current={data.missed.lost} previous={previous?.missed.lost} goodWhenUp={false} />
          }
          sub={
            <>
              {/* «Пропущено» и «потеряно» — разные вещи, и это надо
                  проговаривать: окно ещё идёт — клиент ещё может перезвонить. */}
              пропущено {count(data.missed.count)}, из них вернулись:{' '}
              {count(data.missed.recoveredByClient)} сами,{' '}
              {count(data.missed.recoveredByUs + data.missed.manual)} перезвонили мы
              {data.missed.open > 0 && (
                <>
                  <br />
                  <span
                    className="text-amber-600"
                    title="Окно ещё идёт — эти клиенты ещё могут перезвонить"
                  >
                    в работе: {count(data.missed.open)}
                  </span>
                </>
              )}
              {/* Пять исходов не пересекаются, поэтому остаток — это
                  пропущенные, которых фоновая проверка ещё не разбирала.
                  Без этой строки «пропущено 1, потеряно 0, вернулись 0»
                  читается как «всё в порядке», хотя судьба звонка просто
                  неизвестна. */}
              {unclassified > 0 && (
                <>
                  <br />
                  <span className="text-slate-400" title="Фоновая проверка ещё не разобрала их">
                    судьба ещё не определена: {count(unclassified)}
                  </span>
                </>
              )}
              {/* Не подмножество «пропущено» и не слагаемое: «пропущено» —
                  сколько звонило и не взяли, «отработано» — сколько закрыли
                  люди, в том числе чужих звонков. Спрос и заслуга. */}
              {data.missed.handled !== undefined && data.missed.handled > 0 && (
                <>
                  <br />
                  <span
                    className="text-slate-500"
                    title="Сколько пропущенных закрыли люди: перезвонили сами либо поставили отметку. Считается отдельно от «пропущено» — закрыть можно и чужой звонок"
                  >
                    отработано людьми: {count(data.missed.handled)}
                  </span>
                </>
              )}
            </>
          }
        />

        {/* Недозвон — другая работа, чем пропущенный: там виноваты мы и
            управляют люди на линии, здесь никто не виноват и управляют
            временем набора. Одной цифрой это число, по которому нельзя
            принять ни одного решения. */}
        {data.noAnswer && (
          <Tile
            icon={<PhoneOutgoing size={18} className="text-slate-500" />}
            iconClass="bg-slate-100"
            label="Не дозвонились"
            value={count(data.noAnswer.count)}
            sub={
              <>
                {/* Доля от исходящих, а не от всех звонков. */}
                {data.noAnswer.rate === null
                  ? 'За период не было ни одного исходящего'
                  : `${percent(data.noAnswer.rate, 1)} исходящих · дозвонились ${count(
                      data.noAnswer.reached
                    )}`}
                {data.noAnswer.reason && (
                  <>
                    <br />
                    <span className="text-slate-400">
                      занято {count(data.noAnswer.reason.busy)} · не взяли{' '}
                      {count(data.noAnswer.reason.noPickup)} · не соединилось{' '}
                      {count(data.noAnswer.reason.failed)} · сбросили{' '}
                      {count(data.noAnswer.reason.abandoned)}
                    </span>
                  </>
                )}
              </>
            }
          />
        )}

        <Tile
          icon={<Clock size={18} className="text-indigo-500" />}
          iconClass="bg-indigo-50"
          label="Среднее ожидание"
          value={duration(data.waiting.avgSeconds)}
          delta={
            <Delta
              current={data.waiting.avgSeconds}
              previous={previous?.waiting.avgSeconds}
              suffix=" с"
              goodWhenUp={false}
            />
          }
          sub={
            <>
              80% ждали не дольше {duration(data.waiting.p80Seconds)}
              <br />
              уложились в {data.waiting.slaSeconds} с: {percent(data.waiting.withinSla, 1)}
            </>
          }
        />

        {/* Качество идёт следом, в той же сетке и того же размера, что
            остальные: лишнюю ширину оно получает, только когда без неё ряд
            не замостится. */}
        <div className={`card p-5 ${wideQuality}`}>
          <div className="flex items-start justify-between mb-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <Gauge size={18} className="text-amber-600" />
            </div>
            <Delta
              current={data.quality.avgScore}
              previous={previous?.quality.avgScore}
              digits={1}
            />
          </div>

          {data.quality.avgScore === null ? (
            /* Ноль читается как «работают плохо», хотя мы ничего не
               проверяли. Поэтому вместо цифры — что именно отсутствует. */
            <div className="flex items-start gap-3">
              <CircleSlash size={20} className="text-slate-300 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-700">
                  Оценено 0 из {count(data.calls.total)} звонков
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  {data.quality.reason
                    ? qualityReasons[data.quality.reason]
                    : 'оценивать было нечего'}
                  {data.quality.reason === 'no_scorecard' && (
                    <>
                      {' '}
                      — назначьте его в разделе «Справочники → Подразделения», иначе балл не
                      появится ни у одного звонка.
                    </>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="text-2xl font-bold tabular-nums text-slate-800">
                {decimal(data.quality.avgScore, 1)}
              </div>
              <div className="text-xs font-medium text-slate-500 mt-0.5">Средний балл</div>
              <div className="text-[11px] text-slate-400 mt-2">
                оценено {count(data.quality.scoredCalls)} из {count(data.calls.total)} звонков
              </div>
            </>
          )}
        </div>

        <Tile
          icon={<AlertTriangle size={18} className="text-red-500" />}
          iconClass="bg-red-50"
          label="Критических нарушений"
          value={count(data.quality.criticalCount)}
          tone={data.quality.criticalUnreviewed > 0 ? 'danger' : undefined}
          sub={
            data.quality.criticalUnreviewed > 0 ? (
              <span className="text-red-600">
                ещё не разобрано: {count(data.quality.criticalUnreviewed)}
              </span>
            ) : (
              'все разобраны'
            )
          }
        />

        <Tile
          icon={<Cog size={18} className="text-slate-500" />}
          iconClass="bg-slate-100"
          label="Обработано звонков"
          value={count(data.processing.done)}
          tone={data.processing.error > 0 ? 'warning' : undefined}
          progress={data.calls.total > 0 ? data.processing.done / data.calls.total : null}
          sub={
            <>
              {inQueue > 0 && (
                <span className="inline-flex items-center gap-1 mr-2">
                  <Loader2 size={10} className="animate-spin" />в очереди {count(inQueue)}
                </span>
              )}
              {data.processing.awaitingAudio > 0 && (
                <span className="mr-2" title="Запись ещё не приехала со станции">
                  ждут записи {count(data.processing.awaitingAudio)}
                </span>
              )}
              {/* Пропущенный звонок без записи — не сбой конвейера, и в
                  один счётчик с ошибками его сводить нельзя. */}
              {data.processing.skipped > 0 && (
                <span className="mr-2" title="Звонки без записи: пропущенные, короткие, отменённые">
                  без записи {count(data.processing.skipped)}
                </span>
              )}
              {data.processing.error > 0 && (
                <span className="text-red-600">с ошибкой {count(data.processing.error)}</span>
              )}
              {inQueue === 0 &&
                data.processing.error === 0 &&
                data.processing.awaitingAudio === 0 &&
                data.processing.skipped === 0 &&
                'очередь пуста'}
            </>
          }
        />
      </div>
    </div>
  );
}
